import { useQueryClient } from "@tanstack/solid-query";
import { createFileRoute, useNavigate } from "@tanstack/solid-router";
import { IconEllipsisVertical, IconPlus } from "~/components/icons";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import WorksheetTable from "~/components/worksheet/table/worksheet-table";
import {
  businessDetailResource,
  businessesResource,
  useActiveBusinessId,
} from "~/features/business/business.resource";
import {
  type WorksheetsParams,
  WorksheetsResource,
} from "~/features/worksheets/worksheets.resource";
import { withBusinessGuard } from "~/lib/route-guards";

const LIMIT = 20;

export const Route = createFileRoute("/_dashboard/dashboard/worksheets/")({
  ...withBusinessGuard(),
  loader: async ({ context }) => {
    const collection = await context.queryClient.ensureQueryData(
      businessesResource.options(undefined),
    );
    const businessId = collection.activeBusinessId!;
    const defaultParams: WorksheetsParams = { businessId, limit: LIMIT };
    const [worksheets, activeBusiness] = await Promise.all([
      context.queryClient.ensureQueryData(
        WorksheetsResource.options(defaultParams),
      ),
      context.queryClient.ensureQueryData(
        businessDetailResource.options(businessId),
      ),
    ]);

    return { businessId, worksheets, collection, activeBusiness };
  },
  component: WorksheetsPage,
});

function WorksheetsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const data = Route.useLoaderData();
  const businessId = useActiveBusinessId();

  businessDetailResource.seedIfEmpty(
    qc,
    data().businessId,
    data().activeBusiness,
  );

  const defaultParams: WorksheetsParams = {
    businessId: data().businessId,
    limit: LIMIT,
  };

  WorksheetsResource.seedIfEmpty(qc, defaultParams, data().worksheets);

  return (
    <div class="flex flex-1 flex-col gap-6 px-4 sm:px-6 pb-3 pt-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold">Worksheets</h1>
          <p class="text-muted-foreground">
            Build, edit, and track spreadsheet worksheets.
          </p>
        </div>
        <DropdownMenu placement="bottom-end">
          <DropdownMenuTrigger
            as={Button<"button">}
            variant="outline"
            size="icon"
          >
            <IconEllipsisVertical class="size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent class="w-44">
            <DropdownMenuItem
              onClick={() =>
                navigate({
                  to: "/dashboard/worksheets/$worksheetId",
                  params: { worksheetId: "new" },
                })
              }
            >
              <IconPlus class="size-4" />
              Add Worksheet
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <WorksheetTable businessId={businessId() ?? ""} />
    </div>
  );
}
